import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import Card from '@material-ui/core/Card'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { showNotification } from 'react-admin'
import { connect } from 'react-redux'

import surveyQuestionApiService from '../../providers/survey/surveyQuestionApiService'
import { generateShortId } from '../../helpers/generateShortIds'

import '../AssessmentForm/AssessmentFormStyles.css'
import './RatingQuestionsStyles.css'

// Buyer questions are only shown in buyer surveys, seller questions only in seller surveys
const ROLES = [
  { key: 'buyer', title: 'Buyer Questions', hint: 'Shown to the buyer of the Lot.' },
  { key: 'seller', title: 'Seller Questions', hint: 'Shown to the seller of the Lot.' }
]

const makeQuestion = (role) => ({
  _shortId: generateShortId(),
  role,
  title: '',
  weight: '',
  active: true
})

const hasWeight = question => Number(question.weight) > 0

// Weights are optional; once any active question in a role has one, all of them
// need one and they must add up to 100%. Each role is a separate survey, so its
// weights are totalled on their own.
const weightSummary = (questions, role) => {
  const active = questions.filter(question => question.role === role && question.title && question.title.trim() && question.active !== false)
  const total = active.reduce((sum, question) => sum + (Number(question.weight) || 0), 0)
  const used = active.some(hasWeight)
  const valid = !used || (active.every(hasWeight) && Math.abs(total - 100) < 0.01)
  return { active, total: Math.round(total * 100) / 100, used, valid }
}

/**
 * Rating Questions - questions for a single category (Kind).
 *
 * Reached from the category list via Edit. Questions are edited inline with the
 * same +/- icon buttons as the Assessment Form editor. Each question is rated
 * 1-5 stars in the webapp; the overall star rating is the weighted average of
 * the answers, using each question's weight (%).
 */
class RatingQuestionsEdit extends Component {
  constructor (props) {
    super(props)
    this.state = {
      questions: [],
      loaded: false,
      saving: false
    }
    this.addQuestion = this.addQuestion.bind(this)
    this.splitEvenly = this.splitEvenly.bind(this)
    this.save = this.save.bind(this)
  }

  categoryId () {
    // react-admin passes `id`; fall back to the router param
    return this.props.id ||
      (this.props.match && this.props.match.params && this.props.match.params.id)
  }

  async componentDidMount () {
    const questions = await surveyQuestionApiService.getQuestions(this.categoryId())
    // Questions saved before roles existed were shown to both buyer and seller,
    // so list them under both; saving then keeps them in both surveys.
    const withRoles = questions.reduce((list, question) => {
      const roles = question.role ? [question.role] : ROLES.map(role => role.key)
      return list.concat(roles.map(role => ({ ...question, role })))
    }, [])
    this.setState({
      questions: withRoles.map(question => ({
        ...question,
        weight: question.weight == null ? '' : question.weight,
        _shortId: generateShortId()
      })),
      loaded: true
    })
  }

  addQuestion (role) {
    this.setState({ questions: this.state.questions.concat(makeQuestion(role)) })
  }

  removeQuestion (questionIndex) {
    this.setState({
      questions: this.state.questions.filter((item, index) => index !== questionIndex)
    })
  }

  changeQuestion (questionIndex, field, value) {
    this.setState({
      questions: this.state.questions.map((question, index) => {
        return index === questionIndex ? { ...question, [field]: value } : question
      })
    })
  }

  // Spread 100% across a role's active questions, giving any rounding remainder to the last one
  splitEvenly (role) {
    const { active } = weightSummary(this.state.questions, role)
    if (!active.length) return
    const share = Math.floor((100 / active.length) * 100) / 100
    const last = Math.round((100 - share * (active.length - 1)) * 100) / 100
    this.setState({
      questions: this.state.questions.map(question => {
        const position = active.indexOf(question)
        if (position === -1) return question
        return { ...question, weight: position === active.length - 1 ? last : share }
      })
    })
  }

  async save () {
    const invalid = ROLES.find(role => !weightSummary(this.state.questions, role.key).valid)
    if (invalid) {
      this.props.showNotification(`Every active ${invalid.key} question needs a weight, and the ${invalid.key} weights must add up to 100%`, 'warning')
      return
    }

    const payload = this.state.questions
      .filter(question => question.title && question.title.trim())
      .map((question, index) => ({
        title: question.title.trim(),
        role: question.role,
        weight: hasWeight(question) ? Number(question.weight) : null,
        order: index,
        active: question.active !== false
      }))

    this.setState({ saving: true })
    try {
      await surveyQuestionApiService.saveQuestions(this.categoryId(), payload)
      this.props.showNotification('Rating questions saved')
      this.props.history.push('/rating-questions')
    } catch (error) {
      this.setState({ saving: false })
      this.props.showNotification('Could not save rating questions', 'warning')
    }
  }

  // `questionIndex` is the position in state.questions; `position` is within the role's list
  renderQuestion (question, questionIndex, position) {
    return (
      <div className='depth-1 rating-question' key={question._shortId}>
        <TextField
          label={`Question ${position + 1}`}
          className='question-title'
          value={question.title}
          onChange={(e) => this.changeQuestion(questionIndex, 'title', e.target.value)}
        />
        <TextField
          label='Weight'
          type='number'
          className='question-weight'
          value={question.weight}
          disabled={question.active === false}
          inputProps={{ min: 0, max: 100, step: 'any' }}
          InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
          onChange={(e) => this.changeQuestion(questionIndex, 'weight', e.target.value)}
        />
        <FontAwesomeIcon
          icon={question.active !== false ? 'eye' : 'eye-slash'}
          className='is-public'
          title={question.active !== false ? 'Active - shown in surveys' : 'Inactive - hidden from surveys'}
          onClick={() => this.changeQuestion(questionIndex, 'active', question.active === false)}
        />
        <FontAwesomeIcon
          icon='minus-square'
          className='remove-button'
          title='Remove question'
          onClick={() => this.removeQuestion(questionIndex)}
        />
      </div>
    )
  }

  renderWeightTotal (role) {
    const { total, used, valid } = weightSummary(this.state.questions, role)
    return (
      <div className='weight-total'>
        <span className={valid ? 'total ok' : 'total error'}>
          {used ? `Total weight: ${total}%` : 'No weights set - all questions count equally'}
        </span>
        {used && !valid &&
          <span className='total-hint'>Every active question needs a weight, and they must add up to 100%</span>}
        <Button size='small' color='primary' onClick={() => this.splitEvenly(role)}>Split evenly</Button>
      </div>
    )
  }

  renderRole (role) {
    let position = 0
    return (
      <div key={role.key}>
        <div className='depth-0 rating-category'>
          <h3>{role.title}</h3>
          <p className='rating-questions-hint'>{role.hint}</p>
          {this.state.questions.map((question, questionIndex) => {
            if (question.role !== role.key) return null
            return this.renderQuestion(question, questionIndex, position++)
          })}
          <FontAwesomeIcon
            icon='plus-square'
            className='add-button'
            title={`Add ${role.key} question`}
            onClick={() => this.addQuestion(role.key)}
          />
        </div>
        {this.renderWeightTotal(role.key)}
      </div>
    )
  }

  render () {
    if (!this.state.loaded) {
      return (
        <div>
          <p className='page-title'>Rating Questions</p>
        </div>
      )
    }

    return (
      <div>
        <p className='page-title'>Rating Questions</p>
        <Card className='rating-questions-edit'>
          <div className='assesstment-wrapper editable rating-questions'>
            <p className='rating-questions-hint'>
              After a Lot of this stock type is sold, the buyer is shown the Buyer
              Questions and the seller is shown the Seller Questions. Each question is
              rated 1 to 5 stars. The weight is how much (%) a question counts towards the
              overall star rating; the weights in each section must add up to 100%. Leave
              every weight in a section blank to count its questions equally.
            </p>

            {ROLES.map(role => this.renderRole(role))}

            <div className='custom-kind-actions'>
              <Button
                variant='contained'
                color='primary'
                disabled={this.state.saving}
                onClick={this.save}
              >
                Save
              </Button>
              <Button className='back-button' component={Link} to='/rating-questions'>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }
}

export default connect(null, { showNotification })(RatingQuestionsEdit)
